import FolderOpen from "@mui/icons-material/FolderOpen";
import {
  Button,
  Grid,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import NavigationLock from "./NavigationLock";

import { FilloutStandardEmbed } from "@fillout/react";
import { PageTitleBar } from "common/components";
import { useTranslation } from "common/locales";
import {
  addFilledOut,
  fetchAllProgrammeActions,
  ProgrammeActions,
  selectProgrammeActionsById,
  selectSubmittedActionsEntriesByProgrammeId,
  useAppDispatch,
  useAppSelector,
} from "model";
import {
  setQuestionnaireFinished,
  setQuestionnaireStarted,
} from "model/questionnaireStartedSlice";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import styled, { css } from "styled-components";
import ActionList from "./ActionList";
import CenteredTextBox from "./CenteredTextBox";
import StepCard from "./FirstStepCard";
import ImageWithCowOverlay from "./ImagewitHCowOverlay";

export default function ProgrammeDetailsDrawer() {
  const { programmeId } = useParams<{ programmeId: string }>();
  const REFRESH_INTERVAL = 1 * 1000;
  const programmeActionsQuestionnaireFilledOutIds = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    (state) => (state as any).programmeActionsQuestionnaireFilledOutIds
  );

  const [viewMore, setViewMore] = useState(false);
  const questionnaireStarted = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    (state) => (state as any).questionnaireStarted
  );

  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user.details);
  const history = useHistory();

  const handleBackToFarm = () => {
    history.push(`/file-management-overview`);
  };
  const dispatch = useAppDispatch();

  const programmeActions = useAppSelector((state) =>
    selectProgrammeActionsById(state, programmeId)
  );

  const actionPlanEntries = useAppSelector((state) =>
    selectSubmittedActionsEntriesByProgrammeId(state, programmeId)
  );

  const loading = useAppSelector(
    (state) => state.programmeActions.loading === "pending"
  );
  const hasSelectedActions = actionPlanEntries && actionPlanEntries.length > 0;
  const onSubmit = () => {
    dispatch(addFilledOut({ filledOutProgrammeId: programmeId }));
    dispatch(setQuestionnaireFinished(true));
  };
  useEffect(() => {
    const unsetQuestionnaireStarted = history.listen((e) => {
      if (e.pathname !== `/programme-details/${programmeId}`) {
        // Returning false allows navigation, returning a string shows a prompt
        dispatch(setQuestionnaireFinished(true));
      }
    });

    return function cleanup() {
      unsetQuestionnaireStarted();
    };
  }, [dispatch, history, programmeId]);

  useEffect(() => {
    const onBeforeUnload = (ev: Event) => {
      if (questionnaireStarted.questionnaireStarted) {
        ev.preventDefault();
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaireStarted.questionnaireStarted]);

  const filledOutButResultNotAvailableYet =
    programmeActionsQuestionnaireFilledOutIds.programmeActionsQuestionnaireFilledOutIds.includes(
      programmeId
    ) && !hasSelectedActions;
  useEffect(() => {
    if (filledOutButResultNotAvailableYet) {
      const reloadProgrammesAndActions = async () => {
        if (!loading) {
          await dispatch(fetchAllProgrammeActions());
        }
        return;
      };
      const refreshInterval = setInterval(
        reloadProgrammesAndActions,
        REFRESH_INTERVAL
      );
      // clear interval when there no fields with invalid data
      if (hasSelectedActions) {
        clearInterval(refreshInterval);
      }
      return () => clearInterval(refreshInterval);
    }
  }, [
    dispatch,
    filledOutButResultNotAvailableYet,
    loading,
    hasSelectedActions,
    REFRESH_INTERVAL,
    programmeActions,
  ]);

  if (loading || filledOutButResultNotAvailableYet) {
    return <LinearProgress />;
  }

  const maxHeight = viewMore ? "auto" : 200;

  const hasQuestionnaireUrl = !!programmeActions?.filloutId;

  // Display mode 1A: Actions have been selected/submitted but the result is not available yet because the processing ios not
  if (programmeActions?.submitted === true && actionPlanEntries.length === 0) {
    return (
      <>
        <PageTitleBar
          title={t("programmes.toolbar.title")}
          showDivider
          children={
            <ProfileIcon
              onClick={handleBackToFarm}
              aria-controls="profile-menu"
              aria-haspopup="true"
            >
              <FolderOpen />
            </ProfileIcon>
          }
        />

        {ProgrammeNameAndText(
          programmeActions,
          viewMore,
          maxHeight,
          setViewMore,
          user?.name ?? "",
          t("action.plan.submitted.but.not.available.yet"),
          t
        )}
      </>
    );
  }
  if (hasSelectedActions && programmeActions) {
    return (
      <>
        <PageTitleBar
          title={t("programmes.toolbar.title")}
          showDivider
          children={
            <ProfileIcon
              onClick={handleBackToFarm}
              aria-controls="profile-menu"
              aria-haspopup="true"
            >
              <FolderOpen />
            </ProfileIcon>
          }
        />
        {ProgrammeNameAndText(
          programmeActions,
          viewMore,
          maxHeight,
          setViewMore,
          user?.name ?? "",
          t("action.plan.submitted.thank.you"),
          t,
          false
        )}

        {<ActionList actionPlanEntries={actionPlanEntries} />}
      </>
    );
  } else if (
    hasQuestionnaireUrl &&
    questionnaireStarted.questionnaireStarted &&
    programmeActions
  ) {
    return (
      <>
        <NavigationLock
          when={questionnaireStarted.questionnaireStarted}
        ></NavigationLock>
        {programmesActionsIframe(programmeActions, onSubmit)}
      </>
    );
  } else if (
    hasQuestionnaireUrl &&
    !questionnaireStarted.questionnaireStarted &&
    programmeActions
  ) {
    return (
      <>
        <PageTitleBar
          title={t("programmes.toolbar.title")}
          showDivider
          children={
            <ProfileIcon
              onClick={handleBackToFarm}
              aria-controls="profile-menu"
              aria-haspopup="true"
            >
              <FolderOpen />
            </ProfileIcon>
          }
        />
        {ProgrammeNameAndText(
          programmeActions,
          viewMore,
          maxHeight,
          setViewMore,
          user?.name ?? "",
          programmeActions.welcomeText,
          t
        )}
        <StepCard
          stepNumber={1}
          stepTitle="Plan your actions"
          stepDescription="The first step is to plan actions that feel manageable and will make a real impact on your farm."
          buttonText="PLAN YOUR ACTIONS"
          daysLeft={122}
          status="In progress"
          onButtonClick={() => {
            dispatch(setQuestionnaireStarted(true));
          }}
        />
      </>
    );
  } else if (programmeActions) {
    return (
      <>
        <PageTitleBar
          title={t("programmes.toolbar.title")}
          showDivider
          children={
            <ProfileIcon
              onClick={handleBackToFarm}
              aria-controls="profile-menu"
              aria-haspopup="true"
            >
              <FolderOpen />
            </ProfileIcon>
          }
        />
        {ProgrammeNameAndText(
          programmeActions,
          viewMore,
          maxHeight,
          setViewMore,
          user?.name ?? "",
          programmeActions.welcomeText,
          t
        )}
      </>
    );
  } else {
    return <></>;
  }
}

const ProfileIcon = styled(IconButton)(
  ({ theme }) => `
    background: white;
  :hover {
      background: ${theme.palette.background.default};
    }
    `
);

// Header component replaced with PageTitleBar for consistency
export const programmesActionsIframe = (
  programmesActions: ProgrammeActions,
  onSubmit: () => void
) => {
  return (
    <FilloutStandardEmbed
      filloutId={programmesActions.filloutId || ""}
      onSubmit={onSubmit}
      parameters={programmesActions.filloutParameters}
    />
  );
};

export const ProgrammeNameAndText = (
  programmeActions: ProgrammeActions,
  viewMore: boolean,
  maxHeight: string | number,
  setViewMore: (arg: boolean) => void,
  userName: string,
  text: string,
  t: (a: string) => string,
  readMoreOrLessButton = true
) => {
  return (
    <Grid
      container
      spacing={3}
      sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
    >
      <Grid item xs={12} sm={12} sx={{ textAlign: "center", pb: 8 }}>
        <ImageWithCowOverlay />
      </Grid>

      <Grid item xs={12} sm={12} sx={{ textAlign: "center" }}>
        <Typography
          variant="h4"
          component="h4"
          gutterBottom
          alignContent={"center"}
        >
          {programmeActions.programmeName}
        </Typography>
      </Grid>
      {text && (
        <Grid item xs={12} sm={12}>
          <CenteredTextBox
            text={text.replace("FARMER_NAME", userName)}
            useMaxSpace={viewMore}
            maxHeight={maxHeight}
            lineHeight={20}
          />
        </Grid>
      )}
      {!text && (
        <Grid item xs={12} sm={12}>
          <CenteredTextBox
            text={t("programme.in.developement")}
            useMaxSpace={viewMore}
            maxHeight={maxHeight}
            lineHeight={20}
          />
        </Grid>
      )}
      {text && (
        <Grid
          item
          xs={12}
          sm={12}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignContent: "center",
            alignItems: "center",
          }}
        >
          {readMoreOrLessButton && (
            <Button variant="text" onClick={() => setViewMore(!viewMore)}>
              <MoreLessButton>
                {viewMore ? "Read less" : "Read more"}
              </MoreLessButton>
            </Button>
          )}
        </Grid>
      )}
    </Grid>
  );
};

const MoreLessButton = styled("span")(
  ({ theme }) => css`
    color: var(--Dahlia-700, #2e7831);
    font-feature-settings: "liga" off, "clig" off;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
  `
);
